import {
  collection, doc, addDoc, getDocs, updateDoc, deleteDoc,
  query, where, Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { withTimeout } from './utils';
import { OrgPMTask } from '../models/types';
import { PM_TASK_LIBRARY } from '../modules/pmScheduler/pmReferenceData';

const COLLECTION = 'pmTaskLibraries';



function mapTask(id: string, data: Record<string, any>): OrgPMTask {
  return {
    id,
    orgId: data.orgId ?? '',
    category: data.category ?? '',
    assetType: data.assetType ?? '',
    taskName: data.taskName ?? '',
    trade: data.trade ?? '',
    frequencyValue: data.frequencyValue ?? 1,
    frequencyUnit: data.frequencyUnit ?? 'months',
    estimatedHours: data.estimatedHours ?? 1,
    complianceRequired: data.complianceRequired ?? false,
    complianceStandard: data.complianceStandard ?? undefined,
    createdAt: data.createdAt?.toDate?.()?.toISOString() ?? data.createdAt ?? '',
    seededFromDefault: data.seededFromDefault ?? undefined,
  };
}

export const getOrgTaskLibrary = async (orgId: string): Promise<OrgPMTask[]> => {
  const q = query(collection(db, COLLECTION), where('orgId', '==', orgId));
  const snap = await withTimeout(getDocs(q));
  return snap.docs
    .map(d => mapTask(d.id, d.data()))
    .sort((a, b) => a.category.localeCompare(b.category) || a.assetType.localeCompare(b.assetType) || a.taskName.localeCompare(b.taskName));
};

export const seedOrgTaskLibrary = async (orgId: string, existingTasks: OrgPMTask[]): Promise<void> => {
  const seededIds = new Set(existingTasks.map(t => t.seededFromDefault).filter(Boolean));
  const toSeed = PM_TASK_LIBRARY.filter(t => !seededIds.has(t.id));
  await Promise.all(toSeed.map(t =>
    withTimeout(addDoc(collection(db, COLLECTION), {
      orgId,
      category: t.category,
      assetType: t.assetType,
      taskName: t.taskName,
      trade: t.trade,
      frequencyValue: t.frequencyValue,
      frequencyUnit: t.frequencyUnit,
      estimatedHours: t.estimatedHours,
      complianceRequired: t.complianceRequired,
      ...(t.complianceStandard ? { complianceStandard: t.complianceStandard } : {}),
      seededFromDefault: t.id,
      createdAt: Timestamp.now(),
    }))
  ));
};

export const createOrgTask = async (orgId: string, task: Omit<OrgPMTask, 'id' | 'orgId' | 'createdAt'>): Promise<string> => {
  const ref = await withTimeout(addDoc(collection(db, COLLECTION), {
    orgId,
    category: task.category,
    assetType: task.assetType,
    taskName: task.taskName,
    trade: task.trade,
    frequencyValue: task.frequencyValue,
    frequencyUnit: task.frequencyUnit,
    estimatedHours: task.estimatedHours,
    complianceRequired: task.complianceRequired,
    ...(task.complianceStandard ? { complianceStandard: task.complianceStandard } : {}),
    ...(task.seededFromDefault ? { seededFromDefault: task.seededFromDefault } : {}),
    createdAt: Timestamp.now(),
  }));
  return ref.id;
};

export const updateOrgTask = async (id: string, partial: Partial<Omit<OrgPMTask, 'id' | 'orgId' | 'createdAt'>>): Promise<void> => {
  const data: Record<string, any> = { ...partial };
  if (!data.complianceStandard) delete data.complianceStandard;
  await withTimeout(updateDoc(doc(db, COLLECTION, id), data));
};

export const deleteOrgTask = async (id: string): Promise<void> => {
  await withTimeout(deleteDoc(doc(db, COLLECTION, id)));
};
